import React from 'react';
import { Calculator, Home } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров налога на имущество
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  propertyTypes,
  regionTaxRates
}) => {
  // Расчет текущей кадастровой стоимости
  const cadastralValue = parseFloat(formData.cadastralValue) || 0;
  
  // Получаем выбранный регион
  const selectedRegion = regionTaxRates.find(region => region.id === formData.region) || regionTaxRates[0];
  
  // Обработчик изменения кадастровой стоимости
  const handleCadastralValueChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      cadastralValue: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.cadastralValue) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.cadastralValue;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения площади объекта
  const handleAreaChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      area: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.area) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.area;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения доли владения
  const handleOwnershipShareChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      ownershipShare: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.ownershipShare) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.ownershipShare;
        return newErrors;
      });
    }
  };
  
  // Предопределенные суммы кадастровой стоимости для быстрого выбора
  const predefinedValues = [
    { value: '2000000', label: '2 млн' },
    { value: '5000000', label: '5 млн' },
    { value: '10000000', label: '10 млн' },
    { value: '20000000', label: '20 млн' }
  ];
  
  // Предопределенные площади объекта для быстрого выбора
  const predefinedAreas = [
    { value: '30', label: '30 м²' },
    { value: '50', label: '50 м²' },
    { value: '75', label: '75 м²' },
    { value: '100', label: '100 м²' }
  ];
  
  // Предопределенные доли владения для быстрого выбора
  const predefinedShares = [
    { value: '1', label: '100%' },
    { value: '0.5', label: '50%' },
    { value: '0.33', label: '33%' },
    { value: '0.25', label: '25%' }
  ];
  
  // Функция расчета
  const calculateTax = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый расчет налога
      </h3>
      
      <div className="space-y-5">
        {/* Тип имущества */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Тип имущества</div>
          <div className="grid grid-cols-2 gap-2">
            {propertyTypes.slice(0, 4).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, propertyType: type.id }))}
                className={`p-2 text-xs rounded-lg border transition-colors
                  ${formData.propertyType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="flex items-center justify-center">
                  <Home className="w-3 h-3 mr-1" />
                  <span>{type.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кадастровая стоимость */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="cadastralValueSlider" className="text-gray-600 dark:text-gray-400">Кадастровая стоимость</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(cadastralValue, 0)}</span>
          </div>
          <input
            type="range"
            id="cadastralValueSlider"
            min="500000"
            max="30000000"
            step="100000"
            value={cadastralValue}
            onChange={handleCadastralValueChange}
            aria-label="Кадастровая стоимость"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedValues.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, cadastralValue: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.cadastralValue === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Площадь объекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="areaSlider" className="text-gray-600 dark:text-gray-400">Площадь (кв.м.)</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.area} м²
            </span>
          </div>
          <input
            type="range"
            id="areaSlider"
            min="10"
            max="200"
            step="1"
            value={formData.area}
            onChange={handleAreaChange}
            aria-label="Площадь объекта (кв.м.)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными площадями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAreas.map(area => (
              <button
                key={area.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, area: area.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.area === area.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {area.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Доля в собственности */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="ownershipShareSlider" className="text-gray-600 dark:text-gray-400">Доля владения</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {(parseFloat(formData.ownershipShare) * 100 || 0).toFixed(0)}%
            </span>
          </div>
          <input
            type="range"
            id="ownershipShareSlider"
            min="0.1"
            max="1"
            step="0.01"
            value={formData.ownershipShare}
            onChange={handleOwnershipShareChange}
            aria-label="Доля владения"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными долями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedShares.map(share => (
              <button
                key={share.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, ownershipShare: share.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.ownershipShare === share.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {share.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор региона */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Регион</div>
          <div className="grid grid-cols-1 gap-2">
            {regionTaxRates.map(region => (
              <button
                key={region.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, region: region.id }))}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.region === region.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {region.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Налоговые льготы */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Налоговые льготы</div>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                hasPrivileges: !prev.hasPrivileges,
                privilegeType: !prev.hasPrivileges ? 'pensioner' : 'none'
              }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.hasPrivileges 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">{formData.hasPrivileges ? 'С учетом льгот' : 'Без учета льгот'}</div>
              <div className="text-xs opacity-75">{formData.hasPrivileges ? 'Нажмите, чтобы отключить' : 'Нажмите, чтобы включить'}</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTax}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать налог
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;